import { useEffect } from 'react';
import { Advisory } from '../model/advisory';
import { useAppState } from './app-state';
import { useLocation } from 'react-router';

export interface AdvisoriesGlobalState {
    advisories: Advisory[];
}

export interface GlobalAdvisoriesApi {
    advisories: Advisory[];
    add: (advisory: Advisory, setFocusToAdvisory?: boolean) => void;
    remove: (index: number) => void;
    clear: (setFocusToAdvisory?: boolean) => void;
}

function setFocusToAdvisoriesContainer(containerRef?: React.RefObject<HTMLDivElement>) {
    const container = containerRef?.current;
    if (!container) {
        return;
    }

    setTimeout(() => {
        container?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }, 250);
}

export function useRemoveGlobalAdvisory(index: number, containerRef?: React.RefObject<HTMLDivElement>): void {
    const [, setAdvisories] = useAppState('advisories');
    if (index < 0) {
        return;
    }

    setAdvisories((prevState) => {
        const snapshot = [...prevState];
        snapshot.splice(index, 1);

        return [...snapshot];
    });
    setFocusToAdvisoriesContainer(containerRef);
}

export function useGlobalAdvisories(containerRef: React.RefObject<HTMLDivElement>): GlobalAdvisoriesApi {
    const [advisories, setAdvisories] = useAppState('advisories');

    const setFocusToAdvisoriesContainer = (containerRef?: React.RefObject<HTMLDivElement>) => {
        const container = containerRef?.current;
        if (!container) {
            return;
        }

        setTimeout(() => {
            container?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        }, 250);
    };

    const addAdvisory = (advisory: Advisory, setFocusToAdvisory: boolean = true) => {
        if (!advisory) {
            return;
        }

        setAdvisories((prevState) => {
            return [
                ...(prevState || []),
                { ...advisory }
            ];
        });

        if (setFocusToAdvisory) {
            setFocusToAdvisoriesContainer(containerRef);
        }
    };

    const removeAdvisory = (index: number) => {
        if (index < 0) {
            return;
        }

        setAdvisories((prevState) => {
            const snapshot = [...prevState];
            snapshot.splice(index, 1);

            return [...snapshot];
        });
        setFocusToAdvisoriesContainer(containerRef);
    };

    const clearAdvisories = (setFocusToAdvisory: boolean = true) => {
        setAdvisories([]);

        if (setFocusToAdvisory) {
            setFocusToAdvisoriesContainer(containerRef);
        }
    }

    return {
        advisories: advisories,
        add: addAdvisory,
        remove: removeAdvisory,
        clear: clearAdvisories
    };
}

export interface AdvisoriesLocationState {
    advisories: Advisory[];
}

export function useClearAdvisories(api: GlobalAdvisoriesApi) {
    const location = useLocation();


    useEffect(() => {
        const state: AdvisoriesLocationState = location.state;
        const advisories = state?.advisories || [];

        api?.clear?.();
        for (let i = 0; i < advisories.length; i++) {
            if (!advisories[i]) {
                continue;
            }
            api?.add?.(advisories[i]);
        }
    }, [location]);
}